import { useNavigate } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { motion } from "motion/react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";
import { AppConfig } from "@/common/appConfig";
import { Layout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import HashTabs from "@/components/ui/HashTabs";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { ReferralTemplatesSection } from "@/components/ui/referral/ReferralTemplatesSection";
import { SubscriptionOnlyButton } from "@/components/ui/SubscriptionOnlyButton/SubscriptionOnlyButton";
import { TemplatesEmpty } from "@/components/ui/templates/empty-templates";
import { LibraryList } from "@/components/ui/templates/list-library";
import { TemplatesList } from "@/components/ui/templates/list-templates";
import { SpokenShortcuts } from "@/components/ui/templates/spoken-shortcuts";
import { useTemplateList } from "@/lib/template-queries";
import { container } from "@/lib/utils";
import { NewTemplateButton, TemplatesHome } from "./redesign/TemplatesHome";

export const Templates = (): JSX.Element => {
  const { t } = useTranslation();
  const { loading, templates, error, ready, refresh } = useTemplateList();
  const navigate = useNavigate();

  if (AppConfig.enableAppRedesign)
    return (
      <Layout
        navbarTitle={t("templates.title")}
        breadcrumbs={[
          { name: t("templates.title"), href: `/templates`, current: true },
        ]}
        pageHeader={
          templates.length > 0
            ? {
                count: templates.length,
                countLabel: t("templates.home.count", {
                  count: templates.length,
                }),
                actions: <NewTemplateButton compact />,
              }
            : undefined
        }
      >
        <TemplatesHome
          error={error}
          loading={loading}
          ready={ready}
          templates={templates}
          onRetry={() => {
            void refresh();
          }}
        />
      </Layout>
    );

  const MyTemplatesContent = (): JSX.Element => {
    if (error)
      return (
        <div role="alert">
          <p>
            {templates.length > 0
              ? t("templates.listError.stale")
              : t("templates.listError.failed")}
          </p>
          <Button
            onClick={() => {
              void refresh();
            }}
          >
            {t("common.restartList")}
          </Button>
          {templates.length > 0 && <TemplatesList templates={templates} />}
        </div>
      );
    if (templates.length === 0 && ready) {
      // Built-in templates can still be starred or made the default.
      return (
        <div className="mx-auto max-w-5xl p-4">
          <TemplatesList
            emptyYours={
              <div className="rounded-xl border border-dashed py-8">
                <TemplatesEmpty />
              </div>
            }
            templates={[]}
          />
        </div>
      );
    }

    if (loading) {
      return (
        <div className="flex justify-center mt-20">
          <LoadingSpinner size={8} />
        </div>
      );
    }

    if (templates.length > 0) {
      return (
        <motion.div
          animate="show"
          className="mx-auto max-w-5xl space-y-8"
          initial="hidden"
          variants={container}
        >
          <div className="p-4">
            <div className="flex justify-end mb-6">
              <div className="flex items-center">
                <SubscriptionOnlyButton
                  onClick={async () => {
                    await navigate({ to: "/template/new" });
                  }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {t("templates.newTemplateButton")}
                </SubscriptionOnlyButton>
              </div>
            </div>
            <TemplatesList templates={templates} />
          </div>
        </motion.div>
      );
    }

    return <div />;
  };

  const LibraryContent = (): JSX.Element => {
    return (
      <motion.div
        animate="show"
        className="mx-auto max-w-5xl space-y-8"
        initial="hidden"
        variants={container}
      >
        <div className="p-4">
          <LibraryList />
        </div>
      </motion.div>
    );
  };

  return (
    <Layout
      navbarTitle={t("templates.title")}
      breadcrumbs={[
        { name: t("templates.title"), href: `/templates`, current: true },
      ]}
    >
      <div className="h-full bg-background p-6">
        <HashTabs
          tabs={[
            {
              value: "my-templates",
              label: t("templates.myTemplatesTab"),
              content: <MyTemplatesContent />,
            },
            {
              value: "library",
              label: t("templates.libraryTab"),
              content: <LibraryContent />,
            },
            ...(AppConfig.enableReferralTemplates
              ? [
                  {
                    value: "referral-letters",
                    label: t("referral.templates.tab"),
                    content: (
                      <div className="mx-auto max-w-5xl p-4">
                        <ReferralTemplatesSection />
                      </div>
                    ),
                  },
                ]
              : []),
            {
              value: "shortcuts",
              label: t("shortcuts.tab"),
              content: <SpokenShortcuts />,
            },
          ]}
        />
      </div>
    </Layout>
  );
};
